import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Loader2, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";

export default function AIInsights() {
  const [narrative, setNarrative] = useState("");
  const [busy, setBusy] = useState(false);
  const [anomalies, setAnomalies] = useState({ count: 0, anomalies: [] });

  useEffect(() => { api.post("/ai/anomaly").then((r) => setAnomalies(r.data)); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ai/narrative");
      setNarrative(r.data.narrative);
    } catch (err) { toast.error(err?.response?.data?.detail || "AI failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="ai-page" className="space-y-6">
      <div>
        <div className="overline text-emerald-800 flex items-center gap-2"><Sparkles className="w-3 h-3" /> Claude Sonnet 4.5</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">AI Insights</h1>
        <p className="text-sm text-slate-600 mt-2">AI-augmented hotspot analysis, anomaly detection and ESG narrative generation — always grounded in batch-level evidence.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-slate-200 p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-emerald-800">ESG Narrative Generator</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">Public Sustainability Statement</div>
            </div>
            <button data-testid="gen-narrative" onClick={generate} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate
            </button>
          </div>
          {narrative ? (
            <div data-testid="narrative-output" className="border-l-2 border-emerald-700 pl-4 py-2 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">{narrative}</div>
          ) : (
            <div className="border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              <FileText className="w-5 h-5 mx-auto mb-2" />
              No narrative generated yet. AI will draft an ISO 14067-aligned statement from your batch data.
            </div>
          )}
          <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-slate-500">Always reviewed by humans before publication. No greenwashing.</div>
        </div>

        <div className="border border-slate-200 p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-emerald-800">Anomaly Detection</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">{anomalies.count} batch{anomalies.count!==1?"es":""} flagged</div>
            </div>
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div className="space-y-2 max-h-96 overflow-auto">
            {anomalies.anomalies.map((a) => (
              <div key={a.batch_id} data-testid={`anomaly-${a.batch_id}`} className="border border-amber-200 bg-amber-50 p-3">
                <div className="font-mono font-semibold text-slate-900 text-sm">{a.batch_code}</div>
                <div className="text-xs text-slate-600">₱{a.denomination} · {a.substrate}</div>
                <ul className="mt-2 space-y-0.5">
                  {a.reasons.map((r, i) => <li key={i} className="text-xs text-amber-900">• {r}</li>)}
                </ul>
              </div>
            ))}
            {anomalies.count === 0 && <div className="text-sm text-slate-500 italic">No anomalies detected.</div>}
          </div>
        </div>
      </div>

      <div className="border border-slate-200 bg-slate-50 p-6 text-sm">
        <div className="overline text-emerald-800 mb-2">Tip</div>
        <p className="text-slate-700">Open any batch's <span className="font-semibold">Carbon Passport</span> and click <span className="font-semibold">AI Hotspot Analysis</span> for a per-batch interpretation and reduction recommendations from Claude Sonnet 4.5.</p>
      </div>
    </div>
  );
}
